import { useRef, useState } from 'react';
import PassoParticipacoes, {
  type PassoParticipacoesHandle,
} from '@/pages/assistente-evento/components/PassoParticipacoes';
import {
  salvarParticipacoes,
  ErroSalvarParticipacoes,
  type FalhaParticipacao,
} from '@/pages/assistente-evento/salvarParticipacoes';
import { configurarPresencial } from '@/lib/presencial';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import type { FormaParticipar } from '@/pages/assistente-evento/types';

interface Props {
  eventoId: number;
  participacoes: FormaParticipar[];
  slug: string;
  ehIngresso: boolean;
  aoFechar: () => void;
  aoSalvo: () => void;
}

export default function ParticipacoesEditor({
  eventoId,
  participacoes,
  slug,
  ehIngresso,
  aoFechar,
  aoSalvo,
}: Props) {
  const [lista, setLista] = useState<FormaParticipar[]>(() =>
    participacoes.map((p) => ({ ...p, grupos: p.grupos.map((g) => ({ ...g })) })),
  );
  const ref = useRef<PassoParticipacoesHandle>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [falhas, setFalhas] = useState<FalhaParticipacao[]>([]);

  // A chave presencial é aplicada na hora (RPC), independente do botão Salvar.
  async function mudarPresencial(modalidadeId: number, ligado: boolean) {
    await configurarPresencial(modalidadeId, ligado);
  }

  async function salvar() {
    if (!ref.current || !ref.current.validar()) return;
    setSalvando(true);
    setErro(null);
    setFalhas([]);
    try {
      await salvarParticipacoes(eventoId, lista);
      aoSalvo();
      aoFechar();
    } catch (e) {
      if (e instanceof ErroSalvarParticipacoes) {
        setFalhas(e.falhas);
        setErro(
          e.falhas.length === 1
            ? 'Um item não foi salvo. Os outros foram gravados normalmente.'
            : `${e.falhas.length} itens não foram salvos. Os outros foram gravados normalmente.`,
        );
        if (e.lista.length > 0) setLista(e.lista);
      } else {
        setErro(mensagemAmigavel(e));
      }
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="space-y-5">
      {erro && (
        <div className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          <p>{erro}</p>
          {falhas.length > 0 && (
            <ul className="mt-1 list-inside list-disc space-y-0.5">
              {falhas.map((f) => (
                <li key={f.indice}>
                  <strong className="font-medium">
                    {f.nome || `Item ${f.indice + 1}`}
                  </strong>{' '}
                  — {f.mensagem}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <PassoParticipacoes
        ref={ref}
        participacoes={lista}
        slug={slug}
        ehIngresso={ehIngresso}
        onChange={setLista}
        aoMudarPresencial={mudarPresencial}
      />

      <div className="sticky bottom-0 -mx-5 flex gap-2 border-t border-secondary-100 bg-white px-5 py-3">
        <button
          type="button"
          onClick={salvar}
          disabled={salvando}
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
        <button
          type="button"
          onClick={aoFechar}
          disabled={salvando}
          className="cursor-pointer whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-100 disabled:opacity-50"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}